import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Users, MapPin, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import { Link } from 'react-router-dom';

const ClientUpcomingGroupSessions: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const lang = i18n.language;
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMySessions = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    // Get my active registrations
    const { data: regs, error: regsError } = await supabase
      .from('group_event_registrations')
      .select('id, event_id, status, registered_at')
      .eq('client_id', user.id)
      .in('status', ['registered', 'attended', 'waitlisted']);

    if (regsError || !regs || regs.length === 0) {
      setSessions([]);
      setLoading(false);
      return;
    }

    const eventIds = regs.map(r => r.event_id);
    const now = new Date().toISOString();

    // Get upcoming events
    const { data: events } = await supabase
      .from('group_events')
      .select('*')
      .in('id', eventIds)
      .gte('event_date', now)
      .in('status', ['published'])
      .order('event_date', { ascending: true });

    if (!events || events.length === 0) {
      setSessions([]);
      setLoading(false);
      return;
    }

    // Merge registration status
    const regMap = Object.fromEntries(regs.map(r => [r.event_id, r]));
    setSessions(events.map(e => ({
      ...e,
      myRegistration: regMap[e.id],
    })));
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchMySessions(); }, [fetchMySessions]);

  if (loading) return null;
  if (sessions.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('events.myUpcomingSessions', { defaultValue: 'My Upcoming Group Sessions' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sessions.map(event => {
          const title = getBilingualValue(lang, event.title_en, event.title_zh, event.title) || 'Untitled';
          const isWaitlisted = event.myRegistration?.status === 'waitlisted';

          return (
            <div key={event.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-medium text-sm line-clamp-1">{title}</h4>
                {isWaitlisted && (
                  <Badge variant="outline" className="text-xs shrink-0 text-amber-600 border-amber-300">
                    Waitlisted
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>{format(new Date(event.event_date), 'MMM d, yyyy HH:mm')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <span>{event.location_type === 'virtual' ? '🌐 Online' : event.location_type === 'hybrid' ? '🌐📍 Online & In Person' : '📍 ' + (event.location_details || 'In-person')}</span>
              </div>
              <Link to={`/events/${event.id}`}>
                <Button variant="ghost" size="sm" className="text-xs h-7 px-2">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  {t('events.viewDetails', { defaultValue: 'View Details' })}
                </Button>
              </Link>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ClientUpcomingGroupSessions;
